import { api } from "@/lib/api";
import {
  DepartmentDto,
  departmentDtoListSchema,
  DepartmentDtoSchema,
  departmentFromDto,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export const getDepartmentsQueryOptions = queryOptions({
  queryKey: ["departments"],
  queryFn: async () => {
    const result: Array<DepartmentDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.getWithCache("/api/departments", departmentDtoListSchema, {
        query: {
          page: page.toString(),
        },
      });
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(departmentFromDto);
  },
});

export function useGetDepartments<TERROR extends Error>(opts?: {
  onError?: (error: TERROR) => void;
  enabled?: boolean;
}) {
  const { toast } = useToast();

  const q = useQuery({ ...getDepartmentsQueryOptions, enabled: opts?.enabled });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getDepartmentsError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getDepartmentQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["departments", id],
    queryFn: () =>
      api
        .get("/api/departments/$id", DepartmentDtoSchema, {
          pathReplace: { $id: id },
        })
        .then(departmentFromDto),
  });
}

export function useGetDepartment<TERROR extends Error>(
  id: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({
    ...getDepartmentQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getDepartmentDetailError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
